import React, { useRef, useState } from 'react';
import { post, put, deleteRequest } from 'helpers/api';
import Modal from 'components/modal';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import ManagedInput from 'components/form/managed_input';
import WysiwygEditor from 'components/form/wysiwyg_editor';
import axios from 'axios';
import { requestErrors } from 'helpers/api_errors';

type CustomCardModalProps = {
  projectId: number;
  cardId?: number;
  currentTitle?: string;
  currentSubtitle?: string;
  currentBody?: string;
  modalOpen: boolean;
  onCloseModal: () => void;
};

const NEW_CUSTOM_CARD_TITLE = 'Untitled Card';

export default function CustomCardModal(props: CustomCardModalProps) {
  const [cardId, setCardId] = useState(props.cardId);
  const [title, setTitle] = useState(props.currentTitle || '');
  const [subtitle, setSubtitle] = useState(props.currentSubtitle || '');
  const [body, setBody] = useState(props.currentBody || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);
  const isEditing = props.cardId != null;
  const newCardUrl = `/projects/${props.projectId}/client_report/v2_cards`;
  const updateCardUrl = `/projects/${props.projectId}/client_report/v2_cards/${cardId}`;

  const pendingImages = useRef<Map<string, File>>(new Map());

  const customCardParams = (cardBody: string) => ({
    v2_card: {
      cardType: 'custom_card',
      displayCard: true,
      customCardAttributes: {
        title,
        subtitle,
        body: cardBody
      }
    }
  });

  const ensureCardId = async (): Promise<number> => {
    if (cardId) return cardId;

    const data: { clientReportV2Card: { id: number } } = await post(newCardUrl, {
      v2_card: {
        cardType: 'custom_card',
        displayCard: false,
        customCardAttributes: {
          title: title || NEW_CUSTOM_CARD_TITLE,
          body: ''
        }
      }
    });

    setCardId(data.clientReportV2Card.id);
    return data.clientReportV2Card.id;
  };

  const uploadPendingImages = async (id: number, cardBody: string): Promise<string> => {
    if (!cardBody || pendingImages.current.size === 0) return cardBody;

    let raw: { entityMap?: Record<string, { type?: string; data?: { src?: string } }> };
    try {
      raw = JSON.parse(cardBody);
    } catch {
      return cardBody;
    }

    const entityMap = raw.entityMap || {};
    for (const key of Object.keys(entityMap)) {
      const entity = entityMap[key];
      if (entity?.type !== 'IMAGE' || !entity.data?.src) continue;

      const file = pendingImages.current.get(entity.data.src);
      if (!file) continue;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('email_ca', '1');

      const res = await axios.post<{ url: string }>(
        `/projects/${props.projectId}/client_report/v2_cards/${id}/add_image`,
        formData
      );
      entity.data.src = res.data.url;
    }

    return JSON.stringify(raw);
  };

  const releasePendingImages = () => {
    pendingImages.current.forEach((_file, url) => URL.revokeObjectURL(url));
    pendingImages.current.clear();
  };

  const saveCustomCard = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const id = await ensureCardId();
      const persistedBody = await uploadPendingImages(id, body);
      await put(`/projects/${props.projectId}/client_report/v2_cards/${id}`, customCardParams(persistedBody));
      closeModal();
    } catch {
      // Keep the modal open so the user can retry. ensureCardId reuses the card
      // it already created, so a retry completes that card rather than orphaning it.
      setSaving(false);
      setSaveError('Something went wrong saving this card. Please check your images and try again.');
    }
  };

  const uploadImage = (image: File) => {
    const localUrl = URL.createObjectURL(image);
    pendingImages.current.set(localUrl, image);

    return Promise.resolve({ data: { url: localUrl } });
  };

  const deleteCustomCard = (e) => {
    e.preventDefault();
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCustomCard = async () => {
    setDeleting(true);
    setDeleteErrors([]);

    try {
      await deleteRequest(updateCardUrl);
    } catch (error) {
      setDeleteErrors(requestErrors(error, 'The card could not be deleted. Please try again.'));
      return;
    } finally {
      setDeleting(false);
    }

    closeModal();
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteErrors([]);
  };

  const cancelExit = () => {
    setShowExitConfirm(false);
  };

  const modalTitle = () => {
    if (showDeleteConfirm) {
      return 'Confirm Delete';
    }
    return isEditing ? 'Edit Custom Card' : 'Add Custom Card';
  };

  const hasUnsavedChanges = () => {
    return (title !== (props.currentTitle || '') ||
    subtitle !== (props.currentSubtitle || '') ||
    body !== (props.currentBody || ''));
  };

  const handleCloseWithUnsavedCheck = () => {
    if (saving || showDeleteConfirm || showExitConfirm) {
      return;
    } else if (hasUnsavedChanges()) {
      setShowExitConfirm(true);
    } else {
      discardAndClose();
    }
  };

  const closeModal = () => {
    releasePendingImages();
    props.onCloseModal();
  };

  const discardAndClose = async () => {
    if (!isEditing && cardId) {
      try {
        await deleteRequest(updateCardUrl);
      } catch {
        // Swallow a failure so the modal still closes
        // Would rather have an orphaned card than keep the user stuck in the modal
      }
    }
    closeModal();
  };

  const maxTitleLength = 50;

  const renderDeleteConfirm = () => {
    return (
      <div className="d-flex flex-column">
        <p>Are you sure you want to delete this custom card? This action cannot be undone.</p>
        <Errors className="mb-3" errors={deleteErrors} title={false} />
        <div className="d-flex justify-content-end">
          <Button className="me-2" disabled={deleting} label="Cancel" variant="secondary" onClick={cancelDelete}/>
          <Button disabled={deleting} label="Yes, Delete" variant="destructive" onClick={confirmDeleteCustomCard}/>
        </div>
      </div>
    );
  };

  const renderExitConfirm = () => {
    return (
      <div className="d-flex flex-column">
        <p>Are you sure you want to exit without saving changes? This action cannot be undone.</p>
        <div className="d-flex justify-content-end">
          <Button className="mr-2" label="Cancel" variant="secondary" onClick={cancelExit}/>
          <Button label="Yes, Exit" variant="destructive" onClick={discardAndClose}/>
        </div>
      </div>
    );
  };

  const renderMainBody = () => {
    return (
      <>
        <div className={'form-group required'}>
          <label className="control-label text-capitalize">Title *</label>
          <div className="form-input-container">
            <ManagedInput
              className="form-control"
              maxLength={maxTitleLength}
              name="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.title)}
            />
          </div>
        </div>
        <div className={'form-group'}>
          <label className="control-label text-capitalize">Subtitle</label>
          <div className="form-input-container">
            <ManagedInput
              className="form-control"
              maxLength={maxTitleLength}
              name="subtitle"
              type="text"
              value={subtitle}
              onChange={e => setSubtitle(e.subtitle)}
            />
          </div>
        </div>
        <div className="form-group required">
          <label className="control-label">Body *</label>
          <div className="form-input-container">
            <WysiwygEditor
              content={body}
              onChange={setBody}
              onUploadImage={uploadImage}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      className="custom-card-modal"
      show={props.modalOpen}
      onHide={handleCloseWithUnsavedCheck}
    >
      <Modal.Header closeButton={!(showDeleteConfirm || showExitConfirm || saving)}>
        {modalTitle()}
      </Modal.Header>
      <Modal.Body>
        {showDeleteConfirm
          ? renderDeleteConfirm()
          : showExitConfirm
            ? renderExitConfirm()
            : renderMainBody()
        }
      </Modal.Body>
      {(!showDeleteConfirm && !showExitConfirm) && (
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {isEditing && (
                <Button style={{ cursor: 'pointer' }} variant="destructive" onClick={deleteCustomCard}>
                  Delete
                </Button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {saveError && <span className="text-danger small">{saveError}</span>}
              <Button
                disabled={saving}
                style={{ cursor: 'pointer' }}
                variant="secondary"
                onClick={handleCloseWithUnsavedCheck}
              >
                Cancel
              </Button>
              <Button
                disabled={!title.trim() || saving}
                style={{ cursor: 'pointer' }}
                type="submit"
                onClick={saveCustomCard}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}
